import React, { useState, useEffect, useRef } from 'react'
import { User, Users, HelpCircle, LogOut, ChevronRight, ChevronDown, Settings, Target, ClipboardList } from 'lucide-react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import FamilySwitcher from './FamilySwitcher'
import SecureLS from 'secure-ls'
import { logout } from '../../services/api'
import { clearUserData } from '../../utils/userUtils'
import { getUser, removeUser, removeFamilies } from '../../utils/secureStorage'
import { ReactComponent as Logo } from '../../assets/logo-1g2n.svg'
import NotificationBell from './NotificationBell'
import '../../styles/hub-chapters.css'

function Layout({ children }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [user, setUser] = useState(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const ls = new SecureLS({ encodingType: 'aes' })

  const dropdownRef = useRef(null)
  const userDropdownRef = useRef(null)

  useEffect(() => {
    const loggedIn = localStorage.getItem('isLoggedIn') === 'true'
    setIsLoggedIn(loggedIn)
    if (loggedIn) {
      const userData = getUser()
      setUser(userData)
    }

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen)

  const toggleDropdown = (e) => {
    e.stopPropagation()
    setIsDropdownOpen(!isDropdownOpen)
    setIsUserDropdownOpen(false)
  }

  const toggleUserDropdown = (e) => {
    e.stopPropagation()
    setIsUserDropdownOpen(!isUserDropdownOpen)
    setIsDropdownOpen(false)
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const handleLogout = async () => {
    try {
      await logout()
      localStorage.removeItem('isLoggedIn')
      removeUser()
      removeFamilies()
      localStorage.removeItem('familyId')
      localStorage.removeItem('primaryFamilyId')
      ls.remove('authToken')
      clearUserData() // Clear user's first name
      setIsLoggedIn(false)
      setUser(null)
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const handleLogin = () => {
    navigate('/login')
  }

  const isActiveNav = (navItem) => {
    switch (navItem) {
      case 'Board':
        return location.pathname === '/board'
      case 'Dashboard':
        return location.pathname === '/senior-dashboard' || location.pathname === '/dashboard'
      case 'My Life':
        return location.pathname === '/my-life-v2' ||
               location.pathname === '/my-life' ||
               location.pathname === '/milestones' ||
               location.pathname === '/memories'
      case 'Family':
        return location.pathname === '/family-tree' || location.pathname === '/family-hub'
      case 'Time Capsules':
        return location.pathname === '/timecapsule'
      case 'Voice':
        return location.pathname === '/tell-a-story'
      case 'Settings':
        return location.pathname === '/settings'
      default:
        return false
    }
  }

  const handleNavigation = (navItem) => {
    // Close mobile menu after navigation
    setIsMobileMenuOpen(false)

    // If not logged in, redirect to home page
    if (!isLoggedIn) {
      navigate('/')
      return
    }

    // Handle navigation based on nav item
    switch (navItem) {
      case 'Board':
        navigate('/board')
        break
      case 'Dashboard':
        navigate('/dashboard')
        break
      case 'My Life':
        navigate('/my-life')
        break
      case 'Family':
        navigate('/family-hub')
        break
      case 'Time Capsules':
        navigate('/timecapsule')
        break
      case 'Voice':
        navigate('/tell-a-story')
        break
      case 'Settings':
        navigate('/settings')
        break
      default:
        break
    }
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={`flex flex-col flex-1 min-w-0 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-0'}`}>
        {/* New Navigation Header */}
        <header className="bg-[#556B2F] text-white px-2 sm:px-8 py-2 sm:py-3 flex justify-between items-center shadow-md relative z-40 gap-1 sm:gap-2">
          <div className="flex items-center">
            <button
              onClick={() => navigate(isLoggedIn ? '/senior-dashboard' : '/')}
              className="focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#556B2F] rounded"
            >
              <Logo className="h-7 sm:h-10 w-auto cursor-pointer" style={{ fill: 'white' }} />
            </button>
          </div>
          
          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-md hover:bg-[#4A5D23] transition-colors relative z-10 flex-shrink-0"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:block">
            <ul className="flex gap-4 xl:gap-8 list-none">
              {['Board', 'Dashboard', 'My Life', 'Family', 'Time Capsules'].map((item) => (
                <li key={item}>
                  <button
                    onClick={() => handleNavigation(item)}
                    className={`text-white no-underline px-3 xl:px-4 py-2 rounded-md transition-colors hover:bg-[#4A5D23] text-sm xl:text-base ${
                      isActiveNav(item) ? 'bg-[#4A5D23]' : ''
                    }`}
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="absolute top-full left-0 right-0 bg-[#556B2F] lg:hidden z-50">
              <nav className="px-4 py-4">
                <ul className="space-y-2">
                  {['Board', 'Dashboard', 'My Life', 'Family', 'Time Capsules'].map((item) => (
                    <li key={item}>
                      <button
                        onClick={() => handleNavigation(item)}
                        className={`w-full text-left text-white px-4 py-3 rounded-md hover:bg-[#4A5D23] transition-colors ${
                          isActiveNav(item) ? 'bg-[#4A5D23]' : ''
                        }`}
                      >
                        {item}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          )}

          <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-shrink">
            {/* Notification Bell */}
            {isLoggedIn && <NotificationBell />}

            {/* Family Switcher - shown for multi-family users */}
            {isLoggedIn && <FamilySwitcher />}

            {/* User Profile */}
            {isLoggedIn && user ? (
              <div className="relative" ref={userDropdownRef}>
                <button
                  onClick={toggleUserDropdown}
                  className="flex items-center gap-2 p-2 rounded-full hover:bg-[#4A5D23] transition-colors"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#6B8E23] flex items-center justify-center font-bold text-sm sm:text-base">
                    {user.first_name ? user.first_name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <span className="hidden sm:block text-sm lg:text-base">{user.first_name || 'User'}</span>
                  <ChevronDown size={14} style={{ opacity: 0.5 }} />
                </button>
                {isUserDropdownOpen && (
                  <div className="da-dropdown">
                    <div className="da-header">
                      <div className="da-avatar">
                        {user.first_name ? user.first_name.charAt(0).toUpperCase() : 'U'}
                        {user.last_name ? user.last_name.charAt(0).toUpperCase() : ''}
                      </div>
                      <div className="da-info">
                        <div className="da-name">{user.first_name} {user.last_name}</div>
                        <div className="da-email">{user.email}</div>
                      </div>
                    </div>
                    <div className="da-body">
                      <Link to="/profile" className="da-link" onClick={() => setIsUserDropdownOpen(false)}>
                        <div className="da-link-icon"><User size={16} /></div>
                        <div className="da-link-label">My Profile</div>
                        <ChevronRight size={16} className="da-link-chevron" />
                      </Link>
                      <Link to="/settings" className="da-link" onClick={() => setIsUserDropdownOpen(false)}>
                        <div className="da-link-icon"><Settings size={16} /></div>
                        <div className="da-link-label">Settings</div>
                        <ChevronRight size={16} className="da-link-chevron" />
                      </Link>
                      <Link to="/family-hub" className="da-link" onClick={() => setIsUserDropdownOpen(false)}>
                        <div className="da-link-icon"><Users size={16} /></div>
                        <div className="da-link-label">
                          My Family
                          <div className="da-link-sub">Family members connected</div>
                        </div>
                      </Link>
                      <Link to="/milestone-registry" className="da-link" onClick={() => setIsUserDropdownOpen(false)}>
                        <div className="da-link-icon"><Target size={16} /></div>
                        <div className="da-link-label">Milestone Registry</div>
                      </Link>
                      <Link to="/family-manager" className="da-link" onClick={() => setIsUserDropdownOpen(false)}>
                        <div className="da-link-icon"><ClipboardList size={16} /></div>
                        <div className="da-link-label">Family Manager</div>
                      </Link>
                      <Link to="/help" className="da-link" onClick={() => setIsUserDropdownOpen(false)}>
                        <div className="da-link-icon"><HelpCircle size={16} /></div>
                        <div className="da-link-label">Help & Support</div>
                      </Link>
                      <div className="da-divider"></div>
                      <button onClick={handleLogout} className="da-link da-link-danger">
                        <div className="da-link-icon"><LogOut size={16} /></div>
                        <div className="da-link-label">Log Out</div>
                      </button>
                    </div>
                    <div className="da-footer">
                      <div className="da-footer-text"><span>OneGeneration ToNext</span> · Family Legacy Platform</div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button 
                onClick={handleLogin}
                className="text-sm sm:text-base hover:text-[#E8F5E8] transition-colors"
              >
                Login
              </button>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-hidden bg-[#F5F5DC] p-4 sm:p-6 lg:p-8 max-w-full">
          {children}
        </main>
      </div>
    </div>
  )
}

export default Layout
